import { Checkbox, Flex, Link, Text } from "@chakra-ui/react";
import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as ReactRouterLink } from "react-router";

import { AddIcon, ButtonWrapper, MapIcon, RanaButton } from "@nens/ranalib";
import { TenantContext } from "@nens/ranalib";
import { HIDE_PRINT_CLASS } from "@nens/react/components";

import { RanaMap } from "@/lib/rana-map";
import { RanaPopup } from "@/lib/rana-popup/rana-popup";

import { useTenantProjectListMapLayers } from "./hooks/use-tenant-project-list-map-layers";
import { TenantProjectListTable } from "./project-list-table";
import { useTenantProjectList } from "./use-tenant-project-list";

export function TenantProjectList() {
  const intl = useIntl();

  const { tenant } = useContext(TenantContext);
  const mapId = "tenant-project-list";
  const projectListHookReturn = useTenantProjectList(mapId);
  const {
    isLoading,
    projectsOnMap,
    showingMap,
    showMap,
    hideMap,
    canCreateProject,
    onMapMoveEnd,
    handleMapClick,
    selectedProjectIds,
    popup,
    closePopup,
    filterMap,
    setFilterMap,
    bounds,
    hoveredProjectId,
    setHoveredProjectId,
  } = projectListHookReturn;

  const tenantProjectListMapLayers = useTenantProjectListMapLayers({
    projectsOnMap,
    handleMapClick,
    selectedProjectIds,
    hoveredProjectId,
    setHoveredProjectId,
  });

  return (
    <Flex direction="column" gap={4}>
      <Flex justifyContent="flex-end">
        <ButtonWrapper>
          {showingMap ? (
            <RanaButton variant="secondary" leftIcon={<MapIcon />} onClick={hideMap}>
              <FormattedMessage id="button.hide_map" defaultMessage="Hide map" />
            </RanaButton>
          ) : (
            <RanaButton variant="secondary" leftIcon={<MapIcon />} onClick={showMap}>
              <FormattedMessage id="button.show_map" defaultMessage="Show map" />
            </RanaButton>
          )}

          {canCreateProject ? (
            <RanaButton
              as={ReactRouterLink}
              to={`/${tenant}/projects-new`}
              variant="primary"
              leftIcon={<AddIcon />}
            >
              <FormattedMessage id="button.create_project" defaultMessage="Create project" />
            </RanaButton>
          ) : null}
        </ButtonWrapper>
      </Flex>

      <RanaMap
        mapId={mapId}
        bounds={bounds}
        isLoading={isLoading}
        containerProps={{
          rounded: "base",
          borderWidth: "base",
          overflow: "hidden",
          h: "md",
          display: showingMap ? "block" : "none",
        }}
        hideExport
        mapProps={{
          onMoveEnd: onMapMoveEnd,
        }}
        deckGlProps={{
          layers: tenantProjectListMapLayers,
        }}
      >
        <RanaMap.MapScale />

        {popup ? (
          <RanaPopup
            anchor="left"
            header={<FormattedMessage id="popup.projects" defaultMessage="Projects" />}
            point={popup.point}
            onClose={closePopup}
          >
            <Flex flexDir="column" color="brand.clear_creek" gap={2}>
              {popup.projects.map((project) => (
                <Link
                  key={project.id}
                  as={ReactRouterLink}
                  to={`/${tenant}/projects/${project.slug}`}
                >
                  {project.name}
                </Link>
              ))}
            </Flex>
          </RanaPopup>
        ) : null}
        <RanaMap.ControlsBottom className={HIDE_PRINT_CLASS}>
          <Checkbox
            h="small_button"
            px={2}
            title={intl.formatMessage({
              id: "map.filter_projects",
              defaultMessage:
                "Enable this option to only show projects in the list below that are currently within view on the map",
            })}
            isChecked={filterMap}
            onChange={() => setFilterMap(!filterMap)}
          >
            <Text fontSize="sm">
              <FormattedMessage
                id="map.update_results"
                defaultMessage="Update project list while moving"
              />
            </Text>
          </Checkbox>
        </RanaMap.ControlsBottom>
      </RanaMap>
      <TenantProjectListTable
        {...projectListHookReturn}
        getRowProps={({ slug }) => ({ to: `/${tenant}/projects/${slug}` })}
      />
    </Flex>
  );
}
